// src/components/opportunity/OpportunityListView.tsx
//
// Opportunity list — the landing page for the Opportunities mode. Shows
// a status filter, a "New opportunity" button, and a table of every
// opportunity in the active workspace organization (newest activity
// first). Clicking a row opens the OpportunityWorkspace.

import { useEffect, useMemo, useState } from "react";
import {
  OPPORTUNITY_STATUS_LABELS,
  OPPORTUNITY_STATUSES,
  type OpportunityStatus,
} from "@/types/opportunity";
import { listOpportunities } from "@/lib/opportunity/opportunityClient";
import type { OpportunityListItem } from "@/lib/opportunity/opportunityListTypes";
import { useDualReadProductTypes } from "@/hooks/useDualReadProductTypes";
import { useAuth } from "@/contexts/AuthContext";
import { canViewOrgWideSalesRecords } from "@/lib/sales/orgRecordVisibility";
import {
  fetchLeadTransferCandidates,
  type LeadTransferCandidate,
} from "@/lib/lead/transferLeadOwner";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { supabase } from "@/lib/supabase";
import { userCanDeleteOpportunity } from "@/lib/opportunity/opportunityDeleteAccess";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { OpportunityListTable } from "@/components/opportunity/OpportunityListTable";

export type OpportunityListViewProps = {
  onOpenOpportunity: (opportunityId: string) => void;
  onNewOpportunity: () => void;
  /** Optional refresh trigger (incremented externally to force a reload). */
  refreshKey?: number;
};

export function OpportunityListView({
  onOpenOpportunity,
  onNewOpportunity,
  refreshKey,
}: OpportunityListViewProps) {
  const productTypes = useDualReadProductTypes();
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();
  const canFilterOrgWide =
    auth?.status === "ready" && canViewOrgWideSalesRecords((...r) => auth.hasRole(...r));
  const [statusFilter, setStatusFilter] = useState<OpportunityStatus | "all">("all");
  const [archivedFilter, setArchivedFilter] = useState<"active" | "archived" | "all">("active");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [members, setMembers] = useState<LeadTransferCandidate[]>([]);
  const [locations, setLocations] = useState<Array<{ id: string; name: string | null }>>([]);
  const [opportunities, setOpportunities] = useState<OpportunityListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!canFilterOrgWide || !scopedOrgId) return;
    void fetchLeadTransferCandidates(scopedOrgId).then((r) => {
      if (r.data) setMembers(r.data);
    });
    if (!supabase) return;
    void supabase
      .from("locations")
      .select("id, name")
      .eq("organization_id", scopedOrgId)
      .order("name")
      .then(({ data }) => {
        setLocations(
          (data ?? []).map((row) => ({
            id: String(row.id),
            name: (row.name as string | null) ?? null,
          })),
        );
      });
  }, [canFilterOrgWide, scopedOrgId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void listOpportunities({
      status: statusFilter,
      archived: archivedFilter,
      assigned_user_id: assigneeFilter || undefined,
      location_id: locationFilter || undefined,
    }).then((r) => {
      if (cancelled) return;
      if (r.ok) {
        setOpportunities(r.data.opportunities ?? []);
      } else {
        setError(r.error);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, archivedFilter, assigneeFilter, locationFilter, refreshKey, reloadTick]);

  const visibleCount = opportunities.length;
  const filterOptions = useMemo(
    () => [
      { value: "all" as const, label: "All statuses" },
      ...OPPORTUNITY_STATUSES.map((s) => ({ value: s, label: OPPORTUNITY_STATUS_LABELS[s] })),
    ],
    [],
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as OpportunityStatus | "all")}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-100"
            disabled={loading}
          >
            {filterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={archivedFilter}
            onChange={(e) =>
              setArchivedFilter(e.target.value as "active" | "archived" | "all")
            }
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-100"
            disabled={loading}
          >
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="all">All</option>
          </select>
          {canFilterOrgWide ? (
            <>
              <select
                value={assigneeFilter}
                onChange={(e) => setAssigneeFilter(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-100"
                disabled={loading}
              >
                <option value="">All assignees</option>
                {members.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {(m.full_name || m.display_name || m.email || m.user_id).trim()}
                  </option>
                ))}
              </select>
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-100"
                disabled={loading}
              >
                <option value="">All locations</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name || loc.id}
                  </option>
                ))}
              </select>
            </>
          ) : null}
          {!loading && (
            <span className="text-xs text-gray-500 dark:text-zinc-400">
              {visibleCount} {visibleCount === 1 ? "opportunity" : "opportunities"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setReloadTick((t) => t + 1)}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800 disabled:opacity-50"
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            {...{ [FIREFLY_TARGET_ATTR]: "opp-new-opportunity" }}
            onClick={onNewOpportunity}
            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"
          >
            + New opportunity
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {error}
        </div>
      ) : null}

      <div className="space-y-3" {...{ [FIREFLY_TARGET_ATTR]: "opp-list-surface" }}>
        {loading ? (
          <div className="rounded-2xl border border-gray-200 bg-white px-4 py-6 text-sm text-gray-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
            Loading opportunities…
          </div>
        ) : opportunities.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
            No opportunities yet. Click <strong>+ New opportunity</strong> above to create one.
          </div>
        ) : (
          <OpportunityListTable
            opportunities={opportunities}
            onOpenOpportunity={onOpenOpportunity}
            productTypes={productTypes}
            onMutated={() => setReloadTick((t) => t + 1)}
            canDeleteOpportunity={(opp) =>
              auth?.status === "ready" &&
              auth.session?.user?.id != null &&
              userCanDeleteOpportunity(auth.session.user.id, (...r) => auth.hasRole(...r), opp)
            }
          />
        )}
      </div>
    </div>
  );
}
